import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'
import { M365OneDriveInfo, M365UserOneDriveResponse } from '../../types/m365Users'

interface OneDriveSectionProps {
  userId: string
  userPrincipalName: string
  customerId: string
  customerTenantId: string
}

export function OneDriveSection({
  userId,
  userPrincipalName,
  customerId,
  customerTenantId,
}: OneDriveSectionProps) {
  const { getCustomerGraphAccessToken } = useAuth()
  const [oneDrive, setOneDrive] = useState<M365OneDriveInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [needsGlobalAdmin, setNeedsGlobalAdmin] = useState(false)

  useEffect(() => {
    const fetchOneDrive = async () => {
      try {
        setError(null)
        setNeedsGlobalAdmin(false)
        const token = await getCustomerGraphAccessToken(customerTenantId)
        const response = await api.getM365UserOneDrive(token, customerId, userId) as M365UserOneDriveResponse & {
          error?: string
          details?: string
        }

        // Check if response contains permission error details
        if (response.error && (response.error.includes('Access denied') || response.error.includes('Insufficient permissions'))) {
          setNeedsGlobalAdmin(true)
        } else {
          setOneDrive(response.oneDrive)
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load OneDrive info'

        // Check if it's a permission-related error - this is a GDAP role limitation
        if (errorMessage.includes('403') ||
            errorMessage.includes('Access denied') ||
            errorMessage.includes('Forbidden') ||
            errorMessage.includes('delegated admin') ||
            errorMessage.includes('Files.Read.All')) {
          setNeedsGlobalAdmin(true)
        } else {
          setError(errorMessage)
        }
      } finally {
        setLoading(false)
      }
    }
    fetchOneDrive()
  }, [customerId, customerTenantId, userId, getCustomerGraphAccessToken])

  // Format bytes to human-readable size
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Get storage percentage
  const getStoragePercentage = (): number => {
    if (!oneDrive || !oneDrive.quota.total) return 0
    return Math.round((oneDrive.quota.used / oneDrive.quota.total) * 100)
  }

  // Get storage bar color based on state
  const getStorageBarColor = (): string => {
    if (!oneDrive) return 'bg-gray-300'
    switch (oneDrive.quota.state) {
      case 'exceeded':
        return 'bg-red-500'
      case 'critical':
        return 'bg-red-400'
      case 'nearing':
        return 'bg-yellow-400'
      default:
        return 'bg-primary-500'
    }
  }

  // Build Microsoft Admin Center URL for this user's OneDrive
  const getAdminCenterUserUrl = () => {
    return `https://admin.microsoft.com/#/users/:/UserDetails/${encodeURIComponent(userPrincipalName)}/OneDrive`
  }

  if (loading) {
    return (
      <div className="bg-white shadow sm:rounded-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading OneDrive info...</p>
          </div>
        </div>
      </div>
    )
  }

  // Show GDAP limitation message with Admin Center link
  if (needsGlobalAdmin) {
    return (
      <div className="bg-white shadow sm:rounded-lg p-6">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-blue-400 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-800">OneDrive Access via Admin Center</h3>
              <p className="mt-1 text-sm text-blue-700">
                OneDrive data for other users requires Global Administrator GDAP role.
                Use the Microsoft Admin Center to view this user's OneDrive information.
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href={`https://admin.microsoft.com/#/users/:/UserDetails/${encodeURIComponent(userPrincipalName)}/OneDrive`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-sm font-medium rounded-md text-white hover:bg-primary-700"
                >
                  <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  View OneDrive in Admin Center
                </a>
                <a
                  href="https://admin.onedrive.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                  OneDrive Admin Center
                </a>
              </div>

              <p className="mt-4 text-xs text-gray-500">
                Note: SharePoint Administrator GDAP role allows managing OneDrive settings but not reading individual user storage via API.
                Global Administrator role is required for API access to other users' OneDrive data.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white shadow sm:rounded-lg p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!oneDrive) {
    return (
      <div className="bg-white shadow sm:rounded-lg p-6">
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">OneDrive not provisioned</h3>
          <p className="mt-1 text-sm text-gray-500">
            This user does not have OneDrive set up yet.
          </p>
          <div className="mt-4">
            <a
              href={getAdminCenterUserUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Manage in Admin Center
            </a>
          </div>
        </div>
      </div>
    )
  }

  const percentage = getStoragePercentage()

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">OneDrive</h3>
          <a
            href={getAdminCenterUserUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open in Admin Center
          </a>
        </div>

        {/* Storage usage */}
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-primary-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
                <span className="text-sm font-medium text-gray-900">Storage</span>
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                oneDrive.quota.state === 'exceeded' ? 'bg-red-100 text-red-800' :
                oneDrive.quota.state === 'critical' ? 'bg-red-100 text-red-800' :
                oneDrive.quota.state === 'nearing' ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }`}>
                {percentage}% used
              </span>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
              <div
                className={`h-3 rounded-full transition-all ${getStorageBarColor()}`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              ></div>
            </div>

            <div className="flex justify-between text-sm text-gray-600">
              <span>{formatBytes(oneDrive.quota.used)} used</span>
              <span>{formatBytes(oneDrive.quota.total)} total</span>
            </div>

            {oneDrive.quota.remaining > 0 && (
              <p className="mt-2 text-sm text-gray-500">
                {formatBytes(oneDrive.quota.remaining)} available
              </p>
            )}

            {oneDrive.quota.state !== 'normal' && (
              <div className={`mt-3 p-2 rounded-md ${
                oneDrive.quota.state === 'exceeded' || oneDrive.quota.state === 'critical'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-yellow-50 text-yellow-700'
              }`}>
                <div className="flex items-center text-sm">
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  {oneDrive.quota.state === 'exceeded' && 'Storage quota exceeded. User cannot upload new files.'}
                  {oneDrive.quota.state === 'critical' && 'Storage is critically low. User should free up space.'}
                  {oneDrive.quota.state === 'nearing' && 'Storage is nearing capacity.'}
                </div>
              </div>
            )}
          </div>

          {/* File statistics */}
          {(oneDrive.fileCount !== undefined || oneDrive.lastActivityDate) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {oneDrive.fileCount !== undefined && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm text-gray-500">Total Files</span>
                  </div>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {oneDrive.fileCount.toLocaleString()}
                  </p>
                </div>
              )}

              {oneDrive.activeFileCount !== undefined && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    <span className="text-sm text-gray-500">Active Files</span>
                  </div>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {oneDrive.activeFileCount.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">Files active in last 7 days</p>
                </div>
              )}

              {oneDrive.lastActivityDate && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-gray-500">Last Activity</span>
                  </div>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {new Date(oneDrive.lastActivityDate).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* OneDrive URL */}
          <div className="flex items-center justify-between py-3 border-b border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">OneDrive URL</p>
                <p className="text-sm text-gray-500">User's OneDrive location</p>
              </div>
            </div>
            {oneDrive.webUrl ? (
              <a
                href={oneDrive.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:text-primary-800 truncate max-w-xs"
                title={oneDrive.webUrl}
              >
                Open OneDrive
              </a>
            ) : (
              <span className="text-sm text-gray-400">Not available</span>
            )}
          </div>
        </div>

        {/* Info box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-700">
              <p>
                For advanced OneDrive management (sharing settings, sync settings, retention),
                use the{' '}
                <a
                  href="https://admin.onedrive.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline hover:text-blue-800"
                >
                  OneDrive Admin Center
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
